import clsx from 'clsx';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Turnstile, { useTurnstile } from 'react-turnstile';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import SwitchField from '@components/Form/SwitchField';
import TextField from '@components/Form/TextField';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import { SubscriptionFormProps } from '@components/SubscriptionForm';
import validationSchema from '@components/pages/Subscribe/OfferSelection/validationSchema';
import { useAppContext } from '@contexts/AppContext';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { useEventTracking, useUserCreateForSubscribePage } from '@hooks';
import useVeriffSession from '@hooks/useVeriffSession';
import { Theme } from '@interfaces/global';
import { CLOUDFLARE_SITE_KEY } from '@src/env';
import { ClickTarget, TrackingLocation, TypingInput, click, typing } from '@tracking';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/constants';
import { extractQueryParams } from '@utils/queryStringHelper';

interface FormValues {
  email: string;
  gdprOptIn: boolean;
  userEmail?: string;
}

interface SignupFormProps extends Omit<SubscriptionFormProps, 'buttonText'> {
  formLocation: string;
  isDualSubscribePage?: boolean;
  isOfferSelected?: boolean;
}

const SignupForm = ({
  formLocation,
  leadType,
  isDualSubscribePage = false,
  isOfferSelected = true,
  selectedOffer: { code, isPro },
}: SignupFormProps) => {
  const {
    user: { dobVerified, email: existingEmail, isYPEligible, name: existingName },
    setUser,
  } = useSubscribeForm();
  const { isGdprRequired } = useAppContext();
  const { trackEvent } = useEventTracking({ location: TrackingLocation.SignupForm });
  const isTypingEmail = useRef(false);
  const [isSignupFormSubmitted, setIsSignupFormSubmitted] = useState(false);
  const { mutateAsync: createUser, isLoading } = useUserCreateForSubscribePage();
  const { article_id: referringArticleId } = extractQueryParams(['article_id']);
  const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);
  const Button = isPro ? ProButton : PrimaryButton;
  const { startVeriffSession, isVeriffLoading } = useVeriffSession();
  const turnstile = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Passed userEmail to the form for email validation initial state
  const initialState = {
    email: existingEmail || '',
    gdprOptIn: false,
    userEmail: existingEmail,
  };

  useEffect(() => {
    if (setIsSignupFormSubmitted) {
      const isFormSubmitted = existingEmail && ((isYPPlan && dobVerified) || !isYPPlan);
      setIsSignupFormSubmitted(isFormSubmitted);
    }
  }, [dobVerified, existingEmail, isYPEligible, isYPPlan, setIsSignupFormSubmitted]);

  const handleCreateUser = async ({ email, gdprOptIn }: FormValues) => {
    trackEvent(click({ clickTarget: ClickTarget.Continue }));
    const shouldVerifyDBForExistingUser = isYPPlan && existingEmail && !dobVerified;

    if (!existingEmail) {
      try {
        const createUserParams = {
          declined_opt_in: isGdprRequired ? !gdprOptIn : false,
          code,
          email,
          form_location: formLocation,
          lead_type: leadType,
          opt_in_required: isGdprRequired,
          page: window.location.pathname,
          turnstile_token: turnstileToken,
          referring_article_id: referringArticleId,
        };
        const { stripeCustomerId, userId, redirectTo } = await createUser(createUserParams);

        if (redirectTo) {
          window.location.href = redirectTo;
        }

        setUser((prevUser) => ({ ...prevUser, email, id: userId, stripeCustomerId }));

        if (isYPPlan && !dobVerified) {
          startVeriffSession(existingName, userId);
        }
      } catch (error) {
        console.error(error);
      } finally {
        turnstile.reset();
        setTurnstileToken(null);
      }
    } else if (shouldVerifyDBForExistingUser) {
      startVeriffSession();
    }
  };

  const handleTextFieldChange = (input: TypingInput) => {
    const isTypingRef = {
      [TypingInput.Email]: isTypingEmail,
    };
    const isTyping = isTypingRef[input];

    if (!isTyping.current) {
      isTyping.current = true;
      trackEvent(typing({ inputTarget: input }));
    }
  };

  return (
    <div className="transition-all duration-500 ease-in">
      <Formik
        enableReinitialize
        initialValues={initialState}
        onSubmit={handleCreateUser}
        validationSchema={validationSchema}
      >
        {({ handleSubmit }) => (
          <Form
            className="account-info m-auto max-w-md sm:max-w-full"
            noValidate
            onSubmit={handleSubmit}
          >
            <SectionBreakTitle
              title={existingEmail ? '1. Your account' : '1. Create your account'}
              titleClassName="text-black"
              className={clsx('mb-24', {
                'border-0 border-t border-solid': isDualSubscribePage,
                'border-pro-gold': isPro && isDualSubscribePage,
                'border-primary-magenta': !isPro && isDualSubscribePage,
              })}
              color={isPro ? SectionColor.Gold : SectionColor.Magenta}
              isDualSubscribeExperiment={isDualSubscribePage}
            />
            {(!isDualSubscribePage || (isDualSubscribePage && !existingEmail)) && (
              <>
                <TextField
                  className={clsx({
                    'mb-16': isYPPlan,
                  })}
                  name="email"
                  inputClassName={clsx({
                    'border-pro-gold': isPro && isDualSubscribePage,
                    'border-primary-magenta': !isPro && isDualSubscribePage,
                  })}
                  placeholder="Email"
                  disabled={!!existingEmail}
                  onChangeCapture={() => handleTextFieldChange(TypingInput.Email)}
                />
                {isGdprRequired && !existingEmail && (
                  <SwitchField
                    colorTheme={Theme.Light}
                    className="gdpr mt-16"
                    label="Receive breaking news, relevant articles, and special event invites from The Information"
                    name="gdprOptIn"
                  />
                )}
                {isOfferSelected && (
                  <Turnstile
                    className="mt-8"
                    fixedSize
                    theme="light"
                    sitekey={CLOUDFLARE_SITE_KEY}
                    refreshExpired="auto"
                    onSuccess={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                  />
                )}
                <Button
                  as="button"
                  id="signupBtn"
                  disabled={
                    isLoading || isVeriffLoading || isSignupFormSubmitted || !turnstileToken
                  }
                  isLoading={isLoading || isVeriffLoading}
                  type="submit"
                  className={clsx('mt-24 sm:mb-0', {
                    'hover:!border-pro-gold hover:!bg-transparent': isPro && isDualSubscribePage,
                  })}
                  fullWidth
                >
                  Continue
                </Button>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SignupForm;
